/* assets/bag-options.js
   Bag Options only:
   - show/hide adapter & holster panels
   - reset hidden holster state to avoid stale selection
   - compute & update price for:
       BAG_ONLY (bag price)
       BAG_WITH_ADAPTER (bag + adapter)
       BAG_WITH_HOLSTER (bag + selected holster, fallback to min holster)
   Requirements:
   - bag-options.liquid provides:
       #bag-only-options, #adapter-options, #holster-options
       [data-role="price-bag"], [data-role="price-bag-adapter"], [data-role="price-bag-holster"]
*/

(function () {
  function qs(sel, root = document) { return root.querySelector(sel); }
  function qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }

  function getMoneyFormat() {
    const any = qs('[data-money-format]');
    return any?.dataset?.moneyFormat || window.Shopify?.money_format || "${{amount}}";
  }

  function formatMoney(cents) {
    const fmt = getMoneyFormat();
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(Number(cents || 0), fmt);
    }
    // fallback
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }
  

  function getBagConfig() {
    return qs('input[name="bag-config-ui"]:checked')?.value || "";
  }

  // ---------------------------------------------------------------------------
  // Holster price index
  // ---------------------------------------------------------------------------

  // Build variantId -> price map for holsters from window.nffi.product_addons["Holsters"]
  function buildHolsterPriceIndex() {
    const map = new Map();

    const addons = window.nffi && window.nffi.product_addons;
    const holsters = addons && addons["Holsters"];
    if (!Array.isArray(holsters)) return map;

    holsters.forEach(p => {
      const variants = Array.isArray(p?.variants) ? p.variants : [];
      variants.forEach(v => {
        const id = Number(v?.id);
        const price = Number(v?.price); // cents
        if (Number.isFinite(id) && Number.isFinite(price)) {
          map.set(id, price);
        }
      });
    });

    return map;
  }

  let holsterPriceByVariantId = null;
  let holsterMinPriceCents = 0;
  let bagBasePriceCents = 0;
  let adapterBasePriceCents = 0;

  function rebuildHolsterIndexAndMin() {
    holsterPriceByVariantId = buildHolsterPriceIndex();
    holsterMinPriceCents = 0;

    holsterPriceByVariantId.forEach((price) => {
      if (!Number.isFinite(price) || price <= 0) return;
      if (holsterMinPriceCents === 0 || price < holsterMinPriceCents) {
        holsterMinPriceCents = price;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Base prices from current product (reacts to nffi:product:changed)
  // ---------------------------------------------------------------------------

  function refreshPricesFromNffi() {
    const product = window.nffi && window.nffi.product;
    const addons = window.nffi && window.nffi.product_addons;

    bagBasePriceCents = 0;
    if (product && product.price != null) {
      const p = Number(product.price);
      if (Number.isFinite(p)) bagBasePriceCents = p;
    }

    adapterBasePriceCents = 0;
    if (addons && addons["IWB Adapter"] && Array.isArray(addons["IWB Adapter"].variants)) {
      const v = addons["IWB Adapter"].variants[0];
      if (v && v.price != null) {
        const p = Number(v.price);
        if (Number.isFinite(p)) adapterBasePriceCents = p;
      }
    }

    rebuildHolsterIndexAndMin();

    const bagOnlyEl = qs('[data-role="price-bag"]');
    if (bagOnlyEl) {
      bagOnlyEl.textContent = formatMoney(bagBasePriceCents);
    }

    const bagAdapterEl = qs('[data-role="price-bag-adapter"]');
    if (bagAdapterEl) {
      const total = bagBasePriceCents + (adapterBasePriceCents || 0);
      bagAdapterEl.textContent = formatMoney(total);
    }

    const bagHolsterEl = qs('[data-role="price-bag-holster"]');
    if (bagHolsterEl) {      
      bagHolsterEl.dataset.bagPrice = String(bagBasePriceCents);
      bagHolsterEl.dataset.holsterMinPrice = String(holsterMinPriceCents);
    }

    updatePriceBagHolster();
  }

  // ---------------------------------------------------------------------------
  // BAG_WITH_HOLSTER local card price
  // ---------------------------------------------------------------------------

  function updatePriceBagHolster() {
    const el = qs('[data-role="price-bag-holster"]');
    if (!el) return;

    const bagPrice = bagBasePriceCents;
    const minHolsterPrice = holsterMinPriceCents;

    const hid = qs(".firearm-options-container input#selected-holster-id") || qs("#selected-holster-id");
    const selectedId = Number(hid?.value || 0);

    if (!holsterPriceByVariantId) rebuildHolsterIndexAndMin();

    const holsterPrice = (Number.isFinite(selectedId) && holsterPriceByVariantId.has(selectedId))
      ? holsterPriceByVariantId.get(selectedId)
      : minHolsterPrice;

    const total = bagPrice + (holsterPrice || 0);
    el.textContent = formatMoney(total);
  }

  // ---------------------------------------------------------------------------
  // UI panels
  // ---------------------------------------------------------------------------

  function applyBagPanels() {
    const cfg = getBagConfig();
    const bagOnlyBlock = qs("#bag-only-options");    
    const adapterBlock = qs("#adapter-options");
    const holsterBlock = qs("#holster-options");

    if (bagOnlyBlock) bagOnlyBlock.style.display = (cfg === "BAG_ONLY") ? "block" : "none";
    if (adapterBlock) adapterBlock.style.display = (cfg === "BAG_WITH_ADAPTER") ? "block" : "none";
    if (holsterBlock) holsterBlock.style.display = (cfg === "BAG_WITH_HOLSTER") ? "block" : "none";

    updatePriceBagHolster();
    clearBagConfigError();

    document.dispatchEvent(new CustomEvent("nffi:bag-options:changed"));
  }

  function bindBagConfigRadios() {
    qsa('input[name="bag-config-ui"]').forEach(r => {
      r.addEventListener("change", applyBagPanels);
    });
  }

  // firearm-options.js / holster-card 
  function onHolsterSelectionChanged() {
    updatePriceBagHolster();
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    // Only run on pages where bag-options exists
    if (!qs('input[name="bag-config-ui"]')) return;

    refreshPricesFromNffi();

    bindBagConfigRadios();
    applyBagPanels();

    document.addEventListener("nffi:holster-selected", onHolsterSelectionChanged);

    document.addEventListener("nffi:product:changed", function () {
      refreshPricesFromNffi();
    });

    updatePriceBagHolster();
  }

  // ---- Bag config error helpers ----
  function showBagConfigError(message) {
    const el = document.getElementById("bag-config-error");
    if (!el) return;
    el.textContent = message;
    el.classList.add("bag-config__error--visible");
  }

  function clearBagConfigError() {
    const el = document.getElementById("bag-config-error");
    if (!el) return;
    el.textContent = "";
    el.classList.remove("bag-config__error--visible");
  }

  // expose minimal API
  window.NFFI_BagOptions = {
    init,
    getBagConfig,
    applyBagPanels,
    updatePriceBagHolster,
    onHolsterSelectionChanged,
    showBagConfigError,
    clearBagConfigError
  };
})();
